import React from 'react';

// Small floating pill (bottom-right) that tells the member what the invisible
// Cloudflare Turnstile check is doing — "Verifying…" while it runs, a green
// tick once it has passed — branded with the Cloudflare logo. Driven entirely
// by useTurnstile's `status`. Hidden while Turnstile is idle (outside the
// pre-warm window, no captcha open). Cloudflare's real widget stays hidden
// ("interaction-only") and only appears above this pill when it needs a tick.

const STATES = {
  loading:     { tone: 'muted',   icon: 'fa-circle-o-notch fa-spin', text: 'Verifying…' },
  verifying:   { tone: 'muted',   icon: 'fa-circle-o-notch fa-spin', text: 'Verifying…' },
  retrying:    { tone: 'muted',   icon: 'fa-circle-o-notch fa-spin', text: 'Verifying…' },
  interactive: { tone: 'warning', icon: 'fa-hand-pointer-o',         text: 'Tap the box above' },
  ready:       { tone: 'success', icon: 'fa-check-circle',           text: 'Verified' },
  failed:      { tone: 'danger',  icon: 'fa-exclamation-circle',     text: 'Not verified · Retry' },
  unsupported: { tone: 'danger',  icon: 'fa-exclamation-circle',     text: 'Browser not supported' },
};

// Cloudflare mark (simple-icons, CC0), in Cloudflare orange.
const CloudflareLogo = () => (
  <svg
    className="cgc-tsb-cf"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      fill="#F38020"
      d="M16.5088 16.8447c.1475-.5068.0908-.9707-.1553-1.3154-.2246-.3164-.6045-.499-1.0615-.5205l-8.6592-.1123a.1559.1559 0 0 1-.1333-.0713c-.0283-.042-.0351-.0986-.021-.1553.0278-.084.1123-.1484.2036-.1562l8.7359-.1123c1.0351-.0489 2.1601-.8868 2.5537-1.9136l.499-1.3013c.0215-.0561.0293-.1128.0147-.168-.5625-2.5463-2.835-4.4453-5.5499-4.4453-2.5039 0-4.6284 1.6177-5.3876 3.8614-.4927-.3658-1.1187-.5625-1.794-.499-1.2026.119-2.1665 1.083-2.2861 2.2856-.0283.31-.0069.6128.0635.894C1.5683 13.171 0 14.7754 0 16.752c0 .1748.0142.3515.0352.5273.0141.083.0844.1475.1689.1475h15.9814c.0909 0 .1758-.0645.2032-.1553l.12-.4268zm2.7568-5.5634c-.0771 0-.1611 0-.2383.0112-.0566 0-.1054.0415-.127.0976l-.3378 1.1744c-.1475.5068-.0918.9707.1543 1.3164.2256.3164.6055.498 1.0625.5195l1.8437.1133c.0557 0 .1055.0263.1329.0703.0283.043.0351.1074.0214.1562-.0283.084-.1132.1485-.204.1553l-1.921.1123c-1.041.0488-2.1582.8867-2.5527 1.914l-.1406.3585c-.0283.0713.0215.1416.0986.1416h6.5977c.0771 0 .1474-.0489.169-.126.1122-.4082.1757-.837.1757-1.2803 0-2.6025-2.125-4.727-4.7344-4.727"
    />
  </svg>
);

const TurnstileBadge = ({ status, onRetry }) => {
  const state = STATES[status];
  if (!state) return null; // 'idle' or anything unexpected

  const content = (
    <>
      <i className={`fa ${state.icon} cgc-tsb-icon`} aria-hidden="true"></i>
      <span>{state.text}</span>
      <span className="cgc-tsb-divider" aria-hidden="true"></span>
      <CloudflareLogo />
    </>
  );

  // Groups the three "Verifying…" statuses so the pop-in animation only
  // replays when the visible state actually changes.
  const animKey = state.text;

  return (
    <>
      {status === 'failed' ? (
        <button
          key={animKey}
          type="button"
          className={`cgc-tsb cgc-tsb-${state.tone} cgc-tsb-button`}
          onClick={onRetry}
          aria-label="Cloudflare security check not verified. Tap to retry."
        >
          {content}
        </button>
      ) : (
        <div
          key={animKey}
          className={`cgc-tsb cgc-tsb-${state.tone}`}
          role="status"
          aria-live="polite"
          aria-label={`Cloudflare security check: ${state.text}`}
        >
          {content}
        </div>
      )}
      <style>{`
        .cgc-tsb {
          position: fixed;
          right: 16px;
          bottom: 16px;
          z-index: 1061;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 32px;
          padding: 0 10px 0 12px;
          border-radius: 16px;
          border: 1px solid #e0e0e0;
          background: #fff;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          font-size: 13px;
          font-weight: 500;
          line-height: 1;
          white-space: nowrap;
          animation: cgc-tsb-pop 220ms ease-out;
        }
        .cgc-tsb-icon { font-size: 15px; }
        .cgc-tsb-divider {
          width: 1px;
          height: 16px;
          margin: 0 2px;
          background: #e0e0e0;
        }
        .cgc-tsb-cf { width: 22px; height: 22px; flex-shrink: 0; }
        .cgc-tsb-muted   { color: #616161; }
        .cgc-tsb-warning { color: #e65100; border-color: #ffcc80; }
        .cgc-tsb-success { color: #2e7d32; border-color: #a5d6a7; }
        .cgc-tsb-danger  { color: #c62828; border-color: #ef9a9a; }
        .cgc-tsb-button  { cursor: pointer; }
        .cgc-tsb-button:hover { background: #fff5f5; }
        @keyframes cgc-tsb-pop {
          from { opacity: 0; transform: scale(0.85); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
};

export default TurnstileBadge;
